import {
  type CommentThread,
  type CommentThreads,
  type CommentThreadJSON,
  type MutableCommentThread,
  type MutableCommentThreads,
} from '@databases/model/commentThreads';
import * as Y from 'yjs';

import { type LazyValue } from './helpers/lazy-value';
import { SynchronizedArray } from './helpers/synchronized-array';
import { SynchronizedRecord } from './helpers/synchronized-record';
import { type DisposableElement, type WrappedYType } from './helpers/types';

const CommentThreadProperties = {
  threadId: 'ti',
  isResolved: 'ir',
  commentCount: 'cc',
  versionNumber: 'vn',
};

export class YCommentThread
  extends SynchronizedRecord
  implements CommentThread, MutableCommentThread
{
  static mapper(value: unknown): YCommentThread | undefined {
    if (value instanceof Y.Map) {
      return new YCommentThread(value);
    }
  }

  get threadId(): string {
    return this.getString(CommentThreadProperties.threadId, '');
  }

  set threadId(threadId: string) {
    this.set(CommentThreadProperties.threadId, threadId);
  }

  get isResolved(): boolean {
    return this.getBoolean(CommentThreadProperties.isResolved, false);
  }

  set isResolved(isResolved: boolean) {
    this.set(CommentThreadProperties.isResolved, isResolved);
  }

  get commentCount(): number {
    return this.getNumber(CommentThreadProperties.commentCount, 0);
  }

  set commentCount(commentCount: number) {
    this.set(CommentThreadProperties.commentCount, commentCount);
  }

  get versionNumber(): number {
    return this.getNumber(CommentThreadProperties.versionNumber, 0);
  }

  set versionNumber(versionNumber: number) {
    this.set(CommentThreadProperties.versionNumber, versionNumber);
  }

  toJSON(): CommentThreadJSON {
    return {
      threadId: this.threadId,
      isResolved: this.isResolved,
      commentCount: this.commentCount,
      versionNumber: this.versionNumber,
    };
  }

  static fromJSON(json: CommentThreadJSON): YCommentThread {
    return new YCommentThread(
      new Y.Map([
        [CommentThreadProperties.threadId, json.threadId],
        [CommentThreadProperties.isResolved, json.isResolved],
        [CommentThreadProperties.commentCount, json.commentCount],
        [CommentThreadProperties.versionNumber, json.versionNumber],
      ])
    );
  }
}

export class YCommentThreads
  implements CommentThreads, MutableCommentThreads, DisposableElement, WrappedYType
{
  private threadsArray: SynchronizedArray<YCommentThread>;

  constructor(input: Y.Array<unknown> | LazyValue<Y.Array<unknown>>) {
    this.threadsArray = new SynchronizedArray(input, YCommentThread.mapper);
  }

  get threads(): Array<YCommentThread> {
    return this.threadsArray.toArray();
  }

  addThread(thread: CommentThreadJSON): void {
    this.threadsArray.push([YCommentThread.fromJSON(thread)]);
  }

  setThread(thread: CommentThreadJSON): void {
    const index = this.threadsArray
      .toArray()
      .findIndex((t) => t.threadId === thread.threadId);
    if (index === -1) {
      this.addThread(thread);
    } else {
      this.threadsArray.get(index).threadId = thread.threadId;
      this.threadsArray.get(index).isResolved = thread.isResolved;
    }
  }

  deleteThread(index: number): void {
    this.threadsArray.delete(index);
  }

  dispose(): void {
    this.threadsArray.dispose();
  }

  toJSON(): Array<CommentThreadJSON> {
    return this.threadsArray.toArray().map((thread) => thread.toJSON());
  }

  static fromJSON(json: CommentThreadJSON[]): YCommentThreads {
    return new YCommentThreads(
      Y.Array.from<any>(json.map((thread) => YCommentThread.fromJSON(thread).unwrap()))
    );
  }

  unwrap(): Y.AbstractType<any> | undefined {
    return this.threadsArray.unwrap();
  }
}
